import type { MachineId, TokenSpec } from '../../shared/contract.js';
import { ctxFor } from '../modifier.js';
import type { TokenRuntime } from '../token.js';
import { v3, type Vec3 } from '../vec.js';
import { Cabinet, type CabinetHost, type FieldSolid, type TagTable } from './base.js';

/**
 * MÜNZSCHIEBER (coin pusher). Shelf, sweeping pusher bar, and a pile that persists all
 * run — the player's savings account with physics (economy.md).
 *
 * Tag table: EMPTY. Deliberately. Everything interesting here — [Magnet] clumping the
 * pile, [Geist] dropping into its interior, [Zins] ripening it, [Spalt] doubling an
 * avalanche at the lip, [Kleber] building a dam — is pure physics falling out of the
 * grammar. If this cabinet ever needs a tag rule, the tag's physical default is wrong.
 */

export interface SchieberSetzen {
  /** drop x position on the shelf, -5..5 */
  x: number;
  /** how far forward you lean over the glass; defaults to the back of the pile.
   *  Dropping short is how a [Kleber] dam gets built at the lip. */
  z?: number;
  spec: TokenSpec;
}

const SHELF = {
  halfWidth: 5,
  frontZ: -1,
  backZ: 8,
  dropY: 3,
  dropZ: 5.2,
  edgeFallZ: -1.25,
  sideLossX: 5.6,
  /** The bar must only nudge the BACK of the pile. A stroke that plows into the pile
   *  interior shoves a third of the shelf over the lip every cycle — free money, and a
   *  permanently awake 300-body island. Pile back row sits at z≈5.9 (edge 6.4). */
  pusherFront: 6.1,
  pusherBack: 7.0,
  pusherPeriod: 4,
  pileTarget: 300,
  massCap: 420,
} as const;

/** ticks between sediment passes — 0.25 s is far faster than a pile changes shape */
const SEDIMENT_INTERVAL = 20;
/** anything actually MOVING within this distance un-packs the sediment around it.
 *  Force propagates locally through a jammed pile, and anything reaching further (a
 *  [Magnet], a shove) revives coins by impulse anyway — so this can stay tight, and
 *  keeping it tight is most of the tick budget. */
const REVIVE_RADIUS = 1.35;
/** speed above which a coin counts as genuinely in motion rather than merely awake */
const MOVING_SPEED = 0.32;
/** Coins over the lip inside one second that make the spill an avalanche (one event). */
const AVALANCHE_COINS = 8;
/** how far ahead of the bar's face coins are kept live */
const BAR_BAND = 0.8;

export class Muenzschieber extends Cabinet<SchieberSetzen> {
  readonly id: MachineId = 'muenzschieber';
  readonly limitLabel = 'Haufenmasse — über der Grenze birst das Glas und alles zahlt auf einmal.';
  readonly breakMode = 'glass' as const;
  /** empty by design; see the class comment */
  readonly tagTable: TagTable = {};

  private pusher = -1;
  private phase = 0;
  private speedMult = 1;
  private stutterTicks = 0;
  /** ticks at which a token went over the front edge — an avalanche is jackpot-class */
  private edgeCrossings: number[] = [];
  /** latched while one avalanche is still pouring, so it scores as a single event */
  private inAvalanche = false;
  private pileSeeded = false;
  private paidOut = 0;
  private lostSide = 0;
  /** tokens whose near-miss has already been banked — INSTANCE state: module-level
   *  mutable sets leak between twin runs and silently break determinism. */
  private readonly nearMissed = new Set<number>();

  build(): void {
    const phys = this.host.phys;
    const W = SHELF.halfWidth;
    const midZ = (SHELF.frontZ + SHELF.backZ) / 2;
    const halfZ = (SHELF.backZ - SHELF.frontZ) / 2;

    // Shelf: 'ground' — solid to everything (a [Geist] token must not fall through the
    // floor of the machine) but anchorable, because a [Kleber] dam on the shelf lip is
    // one of the combos this cabinet exists to serve.
    phys.addStatic({ kind: 'box', hx: W, hy: 0.25, hz: halfZ, pos: this.w(v3(0, -0.25, midZ)) }, 'ground', 0.55, 0.02);
    // back wall
    phys.addStatic({ kind: 'box', hx: W + 0.4, hy: 1.6, hz: 0.2, pos: this.w(v3(0, 1.6, SHELF.backZ + 0.2)) }, 'shell', 0.4, 0.1);
    // catch floor well below, so escaping tokens have somewhere to be for one tick
    phys.addStatic({ kind: 'box', hx: W + 6, hy: 0.2, hz: halfZ + 6, pos: this.w(v3(0, -3, midZ)) }, 'shell', 0.9, 0.0);

    // The pusher bar: kinematic, sweeping in z. It must be TALLER than the pile — a bar
    // that only reaches the bottom layers shoves them out from under the rest and the
    // pile just slumps backwards instead of advancing. Pile top sits at y≈1.36.
    this.pusher = phys.addKinematicBox(W, 0.9, 0.35, this.w(v3(0, 0.9, SHELF.pusherBack)));
  }

  fieldSolids(): readonly FieldSolid[] {
    return [];
  }

  /** The pile persists all run; seed it once. */
  seedPile(): void {
    if (this.pileSeeded) return;
    this.pileSeeded = true;
    const rng = this.host.rng;
    let placed = 0;
    // start the pile well back of the lip: a pile that spills the moment the run begins
    // is free money, and it also keeps 200 bodies awake for nothing
    for (let layer = 0; layer < 6 && placed < SHELF.pileTarget; layer++) {
      for (let ix = 0; ix < 10 && placed < SHELF.pileTarget; ix++) {
        for (let iz = 0; iz < 6 && placed < SHELF.pileTarget; iz++) {
          const x = -4.5 + ix * 1.02 + (rng.float('pile') - 0.5) * 0.05;
          const z = 0.9 + iz * 1.02 + (rng.float('pile') - 0.5) * 0.05;
          const y = 0.16 + layer * 0.27;
          const spec: TokenSpec = {
            id: -1,
            material: 'kupfer',
            faceValue: 1,
            radius: 0.5,
            tags: [],
          };
          this.host.spawnToken(spec, this.id, this.w(v3(x, y, z)), v3(0, 0, 0));
          placed++;
        }
      }
    }
  }

  /** Verb 1: Setzen — a timed drop position. */
  setzen(p: SchieberSetzen): void {
    if (this.broken) return;
    const x = clamp(p.x, -SHELF.halfWidth + 0.6, SHELF.halfWidth - 0.6);
    const z = clamp(p.z ?? SHELF.dropZ, SHELF.frontZ + 0.4, SHELF.backZ - 0.6);
    const tok = this.host.spawnToken(
      p.spec,
      this.id,
      this.w(v3(x, SHELF.dropY, z)),
      v3(0, -1.5, 0),
    );
    this.host.emit({ t: 'drop', token: tok.id, machine: this.id });
    this.host.pipe.fire(
      'onDrop',
      { ...ctxFor(this.id, this.host.tick, tok), api: this.host.api },
      { token: tok.id },
    );
  }

  /** Verb 3 flavour: GLÜCK buys a pusher stutter-step. */
  override glueckSpend(stream: string, event: string): boolean {
    const ok = super.glueckSpend(stream, event);
    if (ok) this.stutterTicks = Math.round(0.6 / this.host.dt);
    return ok;
  }

  protected escalate(level: number): void {
    // machines.md: the pusher speeds up AND the House seeds richer tokens onto the pile
    this.speedMult = 1 + 0.35 * (level - 1);
    for (let i = 0; i < 12; i++) {
      const x = (this.host.rng.float('haus:seed') - 0.5) * 8;
      const z = 3 + this.host.rng.float('haus:seed') * 3;
      this.host.spawnToken(
        { id: -1, material: 'silber', faceValue: 3, radius: 0.5, tags: [] },
        this.id,
        this.w(v3(x, 2.5 + i * 0.05, z)),
        v3(0, -0.5, 0),
      );
    }
  }

  protected override onShove(strength: number): void {
    // shoving a coin pusher is the classic sin; it also genuinely works a little
    this.phase += 0.12 * strength;
  }

  tickMachine(dt: number): void {
    this.tickShared(dt);
    if (this.broken) return;

    // ---- pusher bar. It only sweeps while the player is at this cabinet: an unattended
    // pusher would print money, and a churning pile keeps 200 bodies awake for nothing.
    if (this.active) {
      if (this.stutterTicks > 0) {
        this.stutterTicks--;
      } else {
        this.phase += (dt / SHELF.pusherPeriod) * this.speedMult;
      }
      const s = 0.5 - 0.5 * Math.cos(this.phase * Math.PI * 2);
      const z = SHELF.pusherBack + (SHELF.pusherFront - SHELF.pusherBack) * s;
      this.host.phys.moveKinematic(this.pusher, this.w(v3(0, 0.9, z)));
    }

    // ---- edge accounting
    const cutoff = this.host.tick - Math.round(1 / dt);
    while (this.edgeCrossings.length && this.edgeCrossings[0]! < cutoff) this.edgeCrossings.shift();

    for (const t of this.host.tokensIn(this.id)) {
      if (t.body < 0) continue;
      // A sleeping coin is not going anywhere. Skipping them is the difference between
      // reading 300 positions out of WASM every tick and reading the few that move.
      const sleeping = this.host.phys.isSleeping(t.body);
      if (sleeping && t.dormant) continue;
      const p = this.host.api.position(t.id);
      if (!p) continue;
      t.dormant = sleeping;
      const local = { x: p.x - this.origin.x, y: p.y - this.origin.y, z: p.z - this.origin.z };

      // off the sides: lost to the House — unless the room says otherwise (Der Deckel)
      if (Math.abs(local.x) > SHELF.sideLossX && local.y < -0.4) {
        const mode = this.escapeMode(t);
        if (mode === 'lose') this.lostSide++;
        this.registerDud(t);
        this.host.escaped(t, this, mode, p);
        continue;
      }
      // off the front edge: this is the payout
      if (local.z < SHELF.edgeFallZ && local.y < 0.1) {
        this.payEdge(t, p);
        continue;
      }
      // fell off the world
      if (local.y < -2.5) {
        this.host.escaped(t, this, this.escapeMode(t), p);
        continue;
      }
      // teetering on the lip without going over: "almost" is banked, not exploited
      if (local.z < SHELF.frontZ + 0.55 && local.z > SHELF.edgeFallZ && sleeping) {
        if (!this.nearMissed.has(t.id)) {
          this.nearMissed.add(t.id);
          this.feedTopf(Math.max(1, Math.round(t.faceValue * 0.5)));
        }
      }
    }
    if (this.host.tick % SEDIMENT_INTERVAL === 0) this.sedimentPass();
    this.checkLimit();
  }

  /**
   * SLEEP MANAGEMENT — the single biggest lever on tick cost (architecture.md).
   *
   * A dense coin pile is one contact island, so without this one dropped coin wakes all
   * 300 bodies and the solver costs ~6 ms/tick. The rule is physical, not arbitrary: a
   * coin that is *covered* by another coin and has stopped moving is packed sediment.
   * It becomes a fixed body — still solid, still part of the pile's shape, but out of
   * the island graph. It revives the instant it is uncovered, approached by something
   * awake, or pushed. So the top of the pile lives and the buried bed does not, which
   * is exactly how a real Münzschieber behaves.
   */
  private sedimentPass(): void {
    const toks = this.host.tokensIn(this.id);
    const n = toks.length;
    if (n === 0) return;

    const pos: (Vec3 | null)[] = new Array(n);
    const moving: boolean[] = new Array(n);
    for (let i = 0; i < n; i++) {
      const t = toks[i]!;
      pos[i] = t.body >= 0 ? this.host.api.position(t.id) : null;
      // Judge by VELOCITY, not by Rapier's sleeping flag. The flag is island-wide, so a
      // pile that is merely "awake" but standing still would never qualify as sediment
      // and the optimisation would never engage — which is exactly what happened.
      if (t.body >= 0 && !t.sedimented) {
        const v = this.host.phys.linvel(t.body);
        moving[i] = Math.abs(v.x) + Math.abs(v.y) + Math.abs(v.z) > MOVING_SPEED;
      } else {
        moving[i] = false;
      }
    }

    const s = 0.5 - 0.5 * Math.cos(this.phase * Math.PI * 2);
    const barFront = SHELF.pusherBack + (SHELF.pusherFront - SHELF.pusherBack) * s - 0.35;

    for (let i = 0; i < n; i++) {
      const t = toks[i]!;
      const p = pos[i];
      if (!p || t.body < 0 || t.stuck) continue;

      let covered = false;
      let disturbed = false;
      for (let j = 0; j < n; j++) {
        if (i === j) continue;
        const q = pos[j];
        if (!q) continue;
        const dx = Math.abs(p.x - q.x);
        const dz = Math.abs(p.z - q.z);
        if (dx < 0.7 && dz < 0.7 && q.y > p.y + 0.14) covered = true;
        if (!disturbed && moving[j]) {
          const dy = p.y - q.y;
          if (dx * dx + dy * dy + dz * dz < REVIVE_RADIUS * REVIVE_RADIUS) disturbed = true;
        }
        if (covered && disturbed) break;
      }

      // the sweeping bar counts as a disturbance too
      const localZ = p.z - this.origin.z;
      if (localZ > barFront - BAR_BAND) disturbed = true;

      if (t.sedimented) {
        if (!covered || disturbed) {
          t.sedimented = false;
          t.dormant = false;
          this.host.phys.setSedimented(t.body, false);
        }
      } else if (covered && !disturbed && !moving[i]) {
        t.sedimented = true;
        this.host.phys.setSedimented(t.body, true);
      }
    }
  }

  /** How many coins are currently packed sediment rather than live bodies. */
  sedimentCount(): number {
    let n = 0;
    for (const t of this.host.tokensIn(this.id)) if (t.sedimented) n++;
    return n;
  }

  private payEdge(t: TokenRuntime, world: Vec3): void {
    this.edgeCrossings.push(this.host.tick);

    /**
     * An avalanche is ONE event, not one per coin.
     *
     * The intent is right — many coins over the lip at once is jackpot-class, which is
     * what [Spalt] splits on and what makes dam-building pay — but the first version
     * asked "are there ≥8 crossings in the last second?" on *every* coin, so once an
     * avalanche started, every remaining coin in it was independently a jackpot. Found by
     * playing, not by reading: a single opening drop on the seeded pile took VERDACHT
     * from 0 to 14, and six drops took it to 58 — the bouncer's threshold is 60, so the
     * House was hunting the player inside twenty seconds of their first machine.
     *
     * The knock-on was worse than the heat. `registerScore(..., jackpot=true)` zeroes DER
     * TOPF, so a twenty-coin avalanche drained the pot twenty times. The pot is the
     * game's answer to near-misses — "almost" is *banked*, not exploited — and it could
     * never fill while its own signature payout was wiping it once per coin.
     *
     * So: the crossing that *starts* the avalanche is the jackpot, and the rest of the
     * cascade pays as ordinary edge coins. Hysteresis (it re-arms only after the window
     * falls back under half the threshold) stops a long, steady spill from strobing
     * between jackpot and not.
     */
    let avalanche = false;
    if (this.edgeCrossings.length >= AVALANCHE_COINS) {
      if (!this.inAvalanche) {
        this.inAvalanche = true;
        avalanche = true;
      }
    } else if (this.edgeCrossings.length <= AVALANCHE_COINS / 2) {
      this.inAvalanche = false;
    }

    this.registerScore(t, avalanche ? 'lawine' : 'kante', t.faceValue, world, avalanche);
    this.paidOut++;
    this.host.removeToken(t.id, 'scored');
  }

  structuralLoad(): number {
    let mass = 0;
    for (const t of this.host.tokensIn(this.id)) mass += t.body >= 0 ? t.mass : 0;
    return mass / SHELF.massCap;
  }

  burstValue(): number {
    let v = this.topf;
    for (const t of this.host.tokensIn(this.id)) v += t.faceValue;
    return v;
  }

  /** Cash out = whatever you already pushed over the edge; the pile stays the pile. */
  protected override collectRemaining(): { muenzen: number; tickets: number } {
    return { muenzen: 0, tickets: 0 };
  }

  /** The pile is visible collateral: the House can withdraw from it at Abrechnung. */
  pileValue(): number {
    let v = 0;
    for (const t of this.host.tokensIn(this.id)) v += t.faceValue;
    return v;
  }

  pileCount(): number {
    return this.host.tokensIn(this.id).length;
  }

  extra(): unknown {
    return {
      pusherZ: SHELF.pusherBack + (SHELF.pusherFront - SHELF.pusherBack) * (0.5 - 0.5 * Math.cos(this.phase * Math.PI * 2)),
      pileCount: this.pileCount(),
      pileValue: this.pileValue(),
      sediment: this.sedimentCount(),
      paidOut: this.paidOut,
      lostSide: this.lostSide,
      speedMult: this.speedMult,
    };
  }
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
